// frontend/src/components/admin/ClasesSection.jsx
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useAdminCRUD, useModal, useConfirm } from "../../hooks/useAdmin";
import { Button } from "../shared/Button";
import { Input } from "../shared/Input";
import { Card } from "../shared/Card";
import { Badge } from "../shared/Badge";
import { Loading } from "../shared/Loading";
import { ErrorMessage } from "../shared/ErrorMessage";
import { Modal } from "../shared/Modal";
import { EmptyState } from "../shared/EmptyState";
import { formatDate, formatTime } from "../../utils/format";
import * as adminApi from "../../services/api";

export function ClasesSection({  primaryColor = "#ea580c" }) {
  const {
    items: clases,
    loading,
    error,
    submitting,
    reload,
    create,
    remove,
  } = useAdminCRUD(
    {
      fetchAll: () => adminApi.listarClasesAdmin(true),
      create: adminApi.crearClase,
      remove: adminApi.eliminarClase,
    }
  );

  const formModal = useModal();
  const inscritosModal = useModal();
  const notaModal = useModal();
  const { confirmAsync } = useConfirm();

  const [notaValue, setNotaValue] = useState("");
  const [notaSubmitting, setNotaSubmitting] = useState(false);

  const reservarModal = useModal();
  const [clientes, setClientes] = useState([]);
  const [clienteSearch, setClienteSearch] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    tipo: "",
    fecha: "",
    hora: "",
    cupos: "10",
  });
  const [formErrors, setFormErrors] = useState({});

  // ---------- filtering
  const clasesFiltradas = useMemo(() => {
    if (!searchTerm.trim()) return clases;
    const term = searchTerm.toLowerCase();
    
    return clases.filter((clase) => {
      const tipo = clase.tipo?.toLowerCase() || "";
      const fecha = formatDate(clase.fecha_hora).toLowerCase();
      const hora = formatTime(clase.fecha_hora);
      const cupos = (clase.cupos ?? "").toString();
      const disponibles = (clase.cupos_disponibles ?? "").toString();

      return (
        tipo.includes(term) ||
        fecha.includes(term) ||
        hora.includes(term) ||
        cupos.includes(term) ||
        disponibles.includes(term)
      );
    });
  }, [clases, searchTerm]);

  const resultsLabel = useMemo(() => {
    if (!searchTerm.trim()) return `Total: ${clases.length}`;
    if (clasesFiltradas.length === 0) return "Sin resultados";
    if (clasesFiltradas.length === clases.length) return `Mostrando todas (${clases.length})`;
    return `Mostrando ${clasesFiltradas.length} de ${clases.length}`;
  }, [searchTerm, clases.length, clasesFiltradas.length]);

  const clientesFiltrados = useMemo(() => {
    if (!clienteSearch.trim()) return clientes;

    const term = clienteSearch.toLowerCase();

    return clientes.filter((c) => {
      const nombre = `${c.first_name || ""} ${c.last_name || ""}`.toLowerCase();
      const username = c.username?.toLowerCase() || "";

      return nombre.includes(term) || username.includes(term);
    });
  }, [clienteSearch, clientes]);


  // ---------- validation
  const validateForm = () => {
    const errors = {};

    if (!formData.tipo?.trim()) errors.tipo = "Tipo de clase es requerido";
    if (!formData.fecha) errors.fecha = "Fecha es requerida";
    if (!formData.hora) errors.hora = "Hora es requerida";

    const cuposNum = Number(formData.cupos);
    if (!Number.isFinite(cuposNum) || cuposNum < 1) errors.cupos = "Cupos debe ser mayor a 0";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleCreate = () => {
    formModal.open();
    setFormData({ tipo: "", fecha: "", hora: "", cupos: "10" });
    setFormErrors({});
  };

  // ---------- create class
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Revisá los campos marcados", { icon: "⚠️", duration: 3500 });
      return;
    }

    const loadingToast = toast.loading("Creando clase...", {
      style: { borderLeft: `4px solid ${primaryColor}` },
    });

    try {
      const payload = {
        tipo: formData.tipo.trim(),
        fecha: formData.fecha,
        hora: formData.hora,
        cupos: Number(formData.cupos),
      };

      const result = await create(payload);

      if (result.success) {
        toast.success("Clase creada", { icon: "✅", duration: 2500 });
        formModal.close();
      } else {
        toast.error(result.error || "Error al crear clase", { icon: "❌", duration: 5000 });
      }
    } catch (err) {
      toast.error("Error inesperado. Intentá nuevamente.", { icon: "❌", duration: 5000 });
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  // ---------- delete class
  const handleDelete = async (clase) => {
    const confirmed = await confirmAsync(
      `¿Eliminar clase de ${clase.tipo}?\n\nSe devolverán los créditos a los ${clase.inscritos_count} inscriptos.`
    );
    if (!confirmed) return;

    const loadingToast = toast.loading("Eliminando clase...", {
      style: { borderLeft: `4px solid ${primaryColor}` },
    });

    try {
      const result = await remove(clase.id);
      if (result.success) {
        toast.success("Clase eliminada", { icon: "✅", duration: 2500 });
        // si estaba abierto el modal de inscritos para esta clase, cerralo
        if (inscritosModal.isOpen && inscritosModal.data?.id === clase.id) inscritosModal.close();
      } else {
        toast.error(result.error || "Error al eliminar clase", { icon: "❌", duration: 5000 });
      }
    } catch (err) {
      toast.error("Error inesperado. Intentá nuevamente.", { icon: "❌", duration: 5000 });
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  // ---------- remove client from class
  const handleRemoverCliente = async (claseId, clienteId, clienteNombre) => {
    const confirmed = await confirmAsync(
      `¿Remover a ${clienteNombre} de esta clase?\n\nSe le devolverá el crédito.`
    );
    if (!confirmed) return;

    const loadingToast = toast.loading("Removiendo cliente...", {
      style: { borderLeft: `4px solid ${primaryColor}` },
    });

    try {
      const result = await adminApi.removerClienteDeClase( claseId, clienteId);
      if (result?.error) {
        toast.error(result.error, { icon: "❌", duration: 5000 });
      } else {
        toast.success("Cliente removido", { icon: "✅", duration: 2500 });
        await reload();

        // Re-abrir modal con datos frescos (si sigue abierto)
        if (inscritosModal.isOpen) {
          const actualizada = clases.find((c) => c.id === claseId);
          if (actualizada) inscritosModal.open(actualizada);
        }
      }
    } catch (err) {
      toast.error("Error inesperado. Intentá nuevamente.", { icon: "❌", duration: 5000 });
    } finally {
      toast.dismiss(loadingToast);
    }
  };

   const handleAbrirReservarCliente = async (clase) => {
  try {
    const usuarios = await adminApi.listarUsuarios();
    setClientes(usuarios);
    reservarModal.open(clase);
  } catch (err) {
    toast.error("Error cargando clientes");
  }
};

const handleReservarCliente = async (claseId, clienteId) => {
  const loadingToast = toast.loading("Reservando cliente...", {
    style: { borderLeft: `4px solid ${primaryColor}` },
  });

  try {
    const result = await adminApi.reservarClienteClase(claseId, clienteId);

    if (result?.error) {
      toast.error(result.error);
    } else {
      toast.success("Cliente reservado");
      reservarModal.close();
      await reload();
    }
  } catch {
    toast.error("Error inesperado");
  } finally {
    toast.dismiss(loadingToast);
  }
};

  const handleVerInscritos = (clase) => {
    inscritosModal.open(clase);
  };

  const handleNotaOpen = (clase) => {
    setNotaValue(clase.nota || "");
    notaModal.open(clase);
  };

  const handleNotaSubmit = async (e) => {
    e.preventDefault();
    if (!notaModal.data) return;
    setNotaSubmitting(true);
    const loadingToast = toast.loading("Guardando nota...", { style: { borderLeft: `4px solid ${primaryColor}` } });
    try {
      const result = await adminApi.actualizarNotaClase(notaModal.data.id, notaValue);
      if (result?.error) {
        toast.error(result.error, { icon: "❌", duration: 5000 });
      } else {
        toast.success("Nota guardada", { icon: "✅", duration: 2500 });
        notaModal.close();
        reload();
      }
    } catch {
      toast.error("Error inesperado. Intentá nuevamente.", { icon: "❌", duration: 5000 });
    } finally {
      toast.dismiss(loadingToast);
      setNotaSubmitting(false);
    }
  };

  // ---------- states
  if (loading && clases.length === 0) return <Loading primaryColor={primaryColor} />;
  if (error) return <ErrorMessage message={error} onRetry={reload} />;

  return (
    <div className="space-y-4">
      <Card
        primaryColor={primaryColor}
        title="Clases"
        subtitle="Programá clases y gestioná inscriptos"
        actions={
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xs text-slate-400">{resultsLabel}</span>
            <Button variant="primary" onClick={handleCreate} primaryColor={primaryColor}>
              Nueva clase
            </Button>
          </div>
        }
      >
        {/* Search */}
        <div className="mb-5">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            <input
              type="text"
              placeholder="Buscar por tipo, fecha, hora o cupos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-3 rounded-xl bg-slate-800/40 border border-slate-700 text-white placeholder-slate-500
                         focus:ring-2 focus:border-transparent transition"
              style={{ "--tw-ring-color": primaryColor }}
            />

            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-white"
                aria-label="Limpiar búsqueda"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            )}
          </div>

          <div className="mt-2 text-xs text-slate-400">
            {searchTerm.trim() ? (
              clasesFiltradas.length === 0 ? (
                <span className="text-red-300">No se encontraron clases</span>
              ) : (
                <span>{resultsLabel}</span>
              )
            ) : (
              <span>{resultsLabel}</span>
            )}
          </div>
        </div>

        {/* List */}
        {clasesFiltradas.length > 0 ? (
          <div className="space-y-4">
            {clasesFiltradas.map((clase) => (
              <ClaseCard
                key={clase.id}
                clase={clase}
                primaryColor={primaryColor}
                onVerInscritos={() => handleVerInscritos(clase)}
                onEliminar={() => handleDelete(clase)}
                onReservarCliente={() => handleAbrirReservarCliente(clase)}
                onEditarNota={() => handleNotaOpen(clase)}
              />
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-700 bg-slate-800/20 p-2">
            <EmptyState
              icon={searchTerm ? "🔍" : "🏋️"}
              title={searchTerm ? "No se encontraron clases" : "No hay clases programadas"}
              message={
                searchTerm
                  ? `No hay clases que coincidan con "${searchTerm}"`
                  : "Creá la primera clase para que los clientes puedan reservar."
              }
              primaryColor={primaryColor}
              action={
                !searchTerm && (
                  <Button variant="primary" onClick={handleCreate} primaryColor={primaryColor}>
                    Crear primera clase
                  </Button>
                )
              }
            />
          </div>
        )}
      </Card>

      {/* Modal - Nueva clase */}
      <Modal isOpen={formModal.isOpen} onClose={formModal.close} title="Nueva clase" primaryColor={primaryColor}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Tipo de clase"
            name="tipo"
            value={formData.tipo}
            onChange={handleInputChange}
            error={formErrors.tipo}
            required
            placeholder="Ej: Spinning, Yoga, Funcional"
            primaryColor={primaryColor}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Fecha"
              name="fecha"
              type="date"
              value={formData.fecha}
              onChange={handleInputChange}
              error={formErrors.fecha}
              required
              primaryColor={primaryColor}
            />
            <Input
              label="Hora"
              name="hora"
              type="time"
              value={formData.hora}
              onChange={handleInputChange}
              error={formErrors.hora}
              required
              primaryColor={primaryColor}
            />
          </div>

          <Input
            label="Cupos"
            name="cupos"
            type="number"
            value={formData.cupos}
            onChange={handleInputChange}
            error={formErrors.cupos}
            required
            min="1"
            primaryColor={primaryColor}
          />

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
            <Button variant="ghost" onClick={formModal.close} type="button" disabled={submitting}>
              Cancelar
            </Button>
            <Button variant="success" type="submit" disabled={submitting}>
              {submitting ? "Creando..." : "Crear clase"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal - Ver inscritos */}
      <Modal isOpen={inscritosModal.isOpen} onClose={inscritosModal.close} title="Clientes inscriptos" primaryColor={primaryColor}>
        {inscritosModal.data && (
          <div className="space-y-4">
            <div className="bg-slate-800/50 border border-slate-700 p-4 rounded-xl">
              <h4 className="text-lg font-bold text-white mb-1">{inscritosModal.data.tipo}</h4>
              <p className="text-slate-300 text-sm">
                {formatDate(inscritosModal.data.fecha_hora)} - {formatTime(inscritosModal.data.fecha_hora)}
              </p>
              <p className="text-sm mt-2" style={{ color: primaryColor }}>
                {inscritosModal.data.inscritos_count}/{inscritosModal.data.cupos} cupos ocupados (
                {inscritosModal.data.porcentaje_ocupacion?.toFixed(0)}%)
              </p>
            </div>

            {inscritosModal.data.inscritos && inscritosModal.data.inscritos.length > 0 ? (
              <div className="space-y-2">
                <h5 className="text-xs font-semibold uppercase tracking-wide" style={{ color: primaryColor }}>
                  Inscriptos ({inscritosModal.data.inscritos.length})
                </h5>

                {inscritosModal.data.inscritos.map((cliente) => (
                  <div
                    key={cliente.id}
                    className="flex justify-between items-center bg-slate-800/50 border border-slate-700 p-3 rounded-xl hover:bg-slate-800 transition-colors"
                  >
                    <div>
                      <p className="text-white font-medium">{cliente.nombre || cliente.username}</p>
                      {cliente.username && cliente.username !== cliente.nombre && (
                        <p className="text-slate-400 text-sm">@{cliente.username}</p>
                      )}
                    </div>

                    <Button
                      variant="danger"
                      onClick={() =>
                        handleRemoverCliente(
                          inscritosModal.data.id,
                          cliente.id,
                          cliente.nombre || cliente.username
                        )
                      }
                      className="text-xs"
                    >
                      Remover
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-10">
                <p className="text-slate-400">No hay clientes inscriptos aún</p>
              </div>
            )}
          </div>
        )}
      </Modal>
      {/* Modal - Nota / Rutina */}
      <Modal isOpen={notaModal.isOpen} onClose={notaModal.close} title="Nota de clase" primaryColor={primaryColor}>
        {notaModal.data && (
          <form onSubmit={handleNotaSubmit} className="space-y-4">
            <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-3">
              <p className="text-xs text-slate-400">Clase</p>
              <p className="text-white font-semibold">{notaModal.data.tipo}</p>
              <p className="text-slate-400 text-sm mt-0.5">
                {formatDate(notaModal.data.fecha_hora)} · {formatTime(notaModal.data.fecha_hora)}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Rutina / Ejercicios
              </label>
              <textarea
                value={notaValue}
                onChange={(e) => setNotaValue(e.target.value)}
                rows={8}
                placeholder={"Ej:\n• Entrada en calor 10 min\n• Sentadillas 4x12\n• Press banca 3x10\n• Abdominales 3x20\n• Vuelta a la calma"}
                className="w-full rounded-xl bg-slate-800/40 border border-slate-700 text-white placeholder-slate-600 p-3 text-sm
                           focus:outline-none focus:ring-2 focus:border-transparent resize-none transition"
                style={{ "--tw-ring-color": primaryColor }}
              />
              <p className="text-xs text-slate-500 mt-1">Los alumnos inscriptos podrán ver esta nota desde su panel.</p>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
              <Button variant="ghost" onClick={notaModal.close} type="button" disabled={notaSubmitting}>
                Cancelar
              </Button>
              <Button variant="primary" type="submit" disabled={notaSubmitting} primaryColor={primaryColor}>
                {notaSubmitting ? "Guardando..." : "Guardar nota"}
              </Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        isOpen={reservarModal.isOpen}
        onClose={reservarModal.close}
        title="Reservar cliente"
        primaryColor={primaryColor}
      >
        <div className="space-y-4">

          <Input
            placeholder="Buscar cliente..."
            value={clienteSearch}
            onChange={(name, value) => setClienteSearch(value)}
          />

          <div className="max-h-80 overflow-y-auto space-y-2">

            {clientesFiltrados.map((cliente) => (
              <div
                key={cliente.id}
                className="flex justify-between items-center bg-slate-800 border border-slate-700 p-3 rounded-xl"
              >
                <div>
                  <p className="text-white">
                    {cliente.first_name} {cliente.last_name}
                  </p>
                  <p className="text-slate-400 text-sm">@{cliente.username}</p>

                  {cliente.suscripcion_activa && (
                    <p className="text-xs text-green-400">
                      Créditos: {cliente.suscripcion_activa.creditos_restantes}
                    </p>
                  )}
                </div>

                <Button
                  variant="success"
                  onClick={() =>
                    handleReservarCliente(reservarModal.data.id, cliente.id)
                  }
                >
                  Reservar
                </Button>
              </div>
            ))}

          </div>
        </div>
      </Modal>
    </div>
  );
}

function ClaseCard({ clase, primaryColor, onVerInscritos, onEliminar, onReservarCliente, onEditarNota }) {
  const ocupacion = clase.porcentaje_ocupacion || 0;
  const tieneNota = !!clase.nota?.trim();

  const getOcupacionColor = () => {
    if (ocupacion >= 90) return "#dc2626";
    if (ocupacion >= 70) return "#f59e0b";
    return "#10b981";
  };

  const ocupacionColor = getOcupacionColor();

  return (
    <Card primaryColor={primaryColor} className="hover:shadow-xl transition-all">
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-xl font-bold text-white">{clase.tipo}</h3>
            <Badge variant={ocupacion >= 90 ? "danger" : ocupacion >= 70 ? "warning" : "success"}>
              {clase.cupos_disponibles} disponibles
            </Badge>
            {tieneNota && (
              <span className="inline-flex items-center gap-1 rounded-full border border-indigo-500/30 bg-indigo-500/10 px-2 py-0.5 text-xs font-medium text-indigo-300">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
                Nota
              </span>
            )}
          </div>

          <div className="space-y-1 text-sm mb-4">
            <p style={{ color: primaryColor }} className="font-semibold">
              {formatDate(clase.fecha_hora)}
            </p>
            <p className="text-slate-300">{formatTime(clase.fecha_hora)}</p>
          </div>

          {tieneNota && (
            <div className="mb-4 rounded-xl border border-slate-700 bg-slate-800/30 px-4 py-3">
              <p className="text-xs font-medium text-slate-400 mb-1">Rutina</p>
              <p className="text-sm text-slate-300 whitespace-pre-line line-clamp-3">{clase.nota}</p>
            </div>
          )}

          <div className="mb-1">
            <div className="flex justify-between text-xs text-slate-400 mb-1">
              <span>Ocupación</span>
              <span>{clase.inscritos_count}/{clase.cupos} ({ocupacion.toFixed(0)}%)</span>
            </div>
            <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-300" style={{ width: `${ocupacion}%`, backgroundColor: ocupacionColor }} />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-1 gap-2 w-full sm:w-36 shrink-0">
          <Button variant="success" onClick={onReservarCliente} className="w-full justify-center text-xs sm:text-sm">
            Reservar cliente
          </Button>
          <Button variant="ghost" onClick={onVerInscritos} className="w-full justify-center text-xs sm:text-sm">
            Ver inscriptos
          </Button>
          <button
            onClick={onEditarNota}
            className="inline-flex w-full items-center justify-center rounded-xl border border-indigo-500/30 bg-indigo-600/10 px-3 py-2 text-xs sm:text-sm font-medium text-indigo-300 transition hover:bg-indigo-600/20 focus:outline-none"
          >
            {tieneNota ? "Editar nota" : "Agregar nota"}
          </button>
          <button
            onClick={onEliminar}
            className="inline-flex w-full items-center justify-center rounded-xl border border-red-500/30 bg-red-600/10 px-3 py-2 text-xs sm:text-sm font-medium text-red-300 transition hover:bg-red-600/20 hover:text-red-200 focus:outline-none"
          >
            Eliminar
          </button>
        </div>
      </div>
    </Card>
  );
}
